import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { 
  Shield, 
  Globe, 
  Plus, 
  AlertTriangle,
  Check, 
  RefreshCw,
  Trash2,
  Pencil,
  X,
  Save,
  Server
} from 'lucide-react';

const MAX_IPS = 2;

export default function ApiSecuritySettings() {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  // IP Management State
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [loadingIps, setLoadingIps] = useState(true);
  const [savingIp, setSavingIp] = useState(false);

  useEffect(() => {
    fetchDomains();
    fetchIps();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/settings/domains');
      setDomains(res.data.allowed_domains || []);
    } catch (err) {
      console.error('Failed to fetch domains:', err);
      setError('Failed to load domain settings');
    } finally {
      setLoading(false);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) {
      setError('Please enter a domain');
      return;
    }

    // Validate format
    const domain = newDomain.trim().toLowerCase();
    if (!domain.startsWith('https://') && !domain.startsWith('http://')) {
      setError('Domain must start with https:// or http://');
      return;
    }

    // Check for localhost with http (allowed)
    if (domain.includes('localhost') && !domain.startsWith('http://')) {
      setError('Localhost must use http:// (e.g., http://localhost:3000)');
      return;
    }

    // Check if already exists
    if (domains.includes(domain)) {
      setError('This domain is already on the list');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.post('/api/settings/domains/add', { domain });
      setDomains(res.data.allowed_domains);
      setNewDomain('');
      setSuccess(`Domain "${domain}" added successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to add domain:', err);
      setError(err.response?.data?.detail || 'Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const removeDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove "${domain}"?`)) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.post('/api/settings/domains/remove', { domain });
      setDomains(res.data.allowed_domains);
      setSuccess(`Domain "${domain}" removed`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to remove domain:', err);
      setError(err.response?.data?.detail || 'Failed to remove domain');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (index, domain) => {
    setEditingIndex(index);
    setEditValue(domain);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const updateDomain = async (oldDomain) => {
    const newDomainValue = editValue.trim().toLowerCase();
    
    if (!newDomainValue) {
      setError('Please enter a domain');
      return;
    }

    if (!newDomainValue.startsWith('https://') && !newDomainValue.startsWith('http://')) {
      setError('Domain must start with https:// or http://');
      return;
    }

    if (newDomainValue.includes('localhost') && !newDomainValue.startsWith('http://')) {
      setError('Localhost must use http:// (e.g., http://localhost:3000)');
      return;
    }

    // If no change, just cancel edit
    if (newDomainValue === oldDomain) {
      cancelEdit();
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.put('/api/settings/domains/update', {
        old_domain: oldDomain,
        new_domain: newDomainValue
      });
      setDomains(res.data.allowed_domains);
      setSuccess(`Domain updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
      cancelEdit();
    } catch (err) {
      console.error('Failed to update domain:', err);
      setError(err.response?.data?.detail || 'Failed to update domain');
    } finally {
      setSaving(false);
    }
  };

  const handleEditKeyPress = (e, oldDomain) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateDomain(oldDomain);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDomain();
    }
  };

  const isLocalhostDomain = (domain) => {
    return domain.toLowerCase().includes('localhost') || domain.includes('127.0.0.1');
  };

  // ==================== IP Management Functions ====================

  const fetchIps = async () => {
    setLoadingIps(true);
    try {
      const res = await api.get('/api/settings/ips');
      setIps(res.data.allowed_ips || []);
    } catch (err) {
      console.error('Failed to fetch IPs:', err);
    } finally {
      setLoadingIps(false);
    }
  };

  const addIp = async () => {
    if (!newIp.trim()) {
      setError('Please enter an IP address');
      return;
    }

    const ip = newIp.trim();

    // Basic IP validation (IPv4)
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::$|^([0-9a-fA-F]{1,4}:){1,7}:$|^:(:([0-9a-fA-F]{1,4})){1,7}$/;
    
    if (!ipv4Pattern.test(ip) && !ipv6Pattern.test(ip)) {
      setError('Invalid IP address format. Use IPv4 (e.g., 192.168.1.1) or IPv6 format.');
      return;
    }

    // Check if already exists
    if (ips.includes(ip)) {
      setError('This IP is already on the list');
      return;
    }

    setSavingIp(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.post('/api/settings/ips/add', { ip });
      setIps(res.data.allowed_ips);
      setNewIp('');
      setSuccess(`IP "${ip}" added successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to add IP:', err);
      setError(err.response?.data?.detail || 'Failed to add IP');
    } finally {
      setSavingIp(false);
    }
  };

  const removeIp = async (ip) => {
    if (!window.confirm(`Are you sure you want to remove "${ip}"?`)) {
      return;
    }

    setSavingIp(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.post('/api/settings/ips/remove', { ip });
      setIps(res.data.allowed_ips);
      setSuccess(`IP "${ip}" removed`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to remove IP:', err);
      setError(err.response?.data?.detail || 'Failed to remove IP');
    } finally {
      setSavingIp(false);
    }
  };

  const handleIpKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addIp();
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" />
          API Security Settings
        </h1>
        <p className="text-gray-600 mt-1">
          Configure domain restrictions for your API credentials
        </p>
      </div>

      {/* Info Banner 
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">How Domain Restrictions Work</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>API requests from browsers include an <code className="bg-blue-100 px-1 rounded">Origin</code> header</li>
              <li>Only requests from authorized domains will be accepted</li>
              <li><strong>Live credentials:</strong> Do not allow localhost (use Sandbox for local development)</li>
              <li><strong>Sandbox credentials:</strong> Allow localhost for testing</li>
              <li>Server-to-server requests (without Origin header) are allowed</li>
            </ul>
          </div>
        </div>
      </div>*/}

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Authorized Domains */}
          <div className="bg-white rounded-lg shadow-sm border">
            {/* Section Header */}
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Website Restrictions
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Authorize Domains for API Access
                </p>
              </div>
              <button
                onClick={fetchDomains}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div className="p-6">
              {/* Add Domain Input - GCP Style */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add a website
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      disabled={saving}
                    />
                  </div>
                  <button
                    onClick={addDomain}
                    disabled={saving || !newDomain.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Example: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">https://myapp.com</code> or 
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 ml-1">http://localhost:3000</code>
                </p>
              </div>

            {/* Domain List - Simple Table */}
            {domains.length === 0 ? (
              <div className="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <Globe className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="font-medium text-gray-600 mb-1">No website restrictions</p>
                <p className="text-sm text-gray-400 mb-4">Add domains to restrict where your API can be used</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 border-b px-4 py-3 flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span>Authorized Websites</span>
                  <span>Actions</span>
                </div>
                
                {/* Table Body */}
                <div className="divide-y divide-gray-100">
                  {domains.map((domain, index) => (
                    <div
                      key={index}
                      className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      {editingIndex === index ? (
                        /* Edit Mode */
                        <>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => handleEditKeyPress(e, domain)}
                              className="flex-1 px-3 py-1.5 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                              autoFocus
                              disabled={saving}
                            />
                          </div>
                          
                          {/* Save/Cancel Actions */}
                          <div className="flex items-center gap-2 ml-3">
                            <button
                              onClick={() => updateDomain(domain)}
                              disabled={saving || !editValue.trim()}
                              className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition disabled:opacity-50"
                              title="Save"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={saving}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition disabled:opacity-50"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      ) : (
                        /* View Mode */
                        <>
                          <div className="flex items-center gap-3 min-w-0">
                            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <p className="font-mono text-sm text-gray-900 truncate" title={domain}>
                              {domain}
                            </p>
                          </div>
                          
                          {/* Edit/Delete Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEdit(index, domain)}
                              disabled={saving || editingIndex !== null}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition disabled:opacity-50"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeDomain(domain)}
                              disabled={saving || editingIndex !== null}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Table Footer */}
                <div className="bg-gray-50 border-t px-4 py-2 text-xs text-gray-500">
                  {domains.length} website{domains.length !== 1 ? 's' : ''} authorized
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Server IP Restrictions */}
          <div className="bg-white rounded-lg shadow-sm border">
            {/* Section Header */}
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Server className="w-5 h-5 text-purple-600" />
                  Server IP Restrictions
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Authorize up to {MAX_IPS} Static IP Addresses for API Access
                </p>
              </div>
              <button
                onClick={fetchIps}
                disabled={loadingIps}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loadingIps ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div className="p-6">
              {/* Add IP Input - Only show if under the IP limit */}
              {ips.length < MAX_IPS && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add a server IP address
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      onKeyPress={handleIpKeyPress}
                      placeholder="192.168.1.1"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-mono"
                      disabled={savingIp}
                    />
                  </div>
                  <button
                    onClick={addIp}
                    disabled={savingIp || !newIp.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add IP
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Enter the public IP address of your server. Example: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">203.0.113.50</code>
                </p>
              </div>
              )}

              {/* IP List */}
              {loadingIps ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : ips.length === 0 ? (
                <div className="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <Server className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium text-gray-600 mb-1">No IP restrictions configured</p>
                  <p className="text-sm text-gray-400 mb-4">Add your server's IP to enable server-to-server API access</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-gray-50 border-b px-4 py-3 flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Authorized Server IP</span>
                    <span>Actions</span>
                  </div>
                  
                  {/* Table Body */}
                  <div className="divide-y divide-gray-100">
                    {ips.map((ip, index) => (
                      <div
                        key={index}
                        className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Server className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <p className="font-mono text-sm text-gray-900 truncate" title={ip}>
                            {ip}
                          </p>
                        </div>
                        
                        {/* Delete Action */}
                        <button
                          onClick={() => removeIp(ip)}
                          disabled={savingIp}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Table Footer */}
                  <div className="bg-gray-50 border-t px-4 py-2 text-xs text-gray-500">
                    {ips.length} of {MAX_IPS} IP{MAX_IPS > 1 ? 's' : ''} authorized
                    {ips.length >= MAX_IPS ? ' (remove one to add a different IP)' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Warning for Production */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Important Notes</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700">
                  <li><strong>Live API credentials</strong> require at least one non-localhost domain</li>
                  <li><strong>Sandbox credentials</strong> allow localhost for local development</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Credential Eligibility Summary */}
          {domains.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Your Domain Configuration Status</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Sandbox Status */}
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-green-800">Sandbox Credentials</span>
                  </div>
                  <p className="text-sm text-green-700">
                    Ready to generate! You can use sandbox credentials for testing.
                  </p>
                </div>
                
                {/* Live Status */}
                {domains.some(d => !isLocalhostDomain(d)) ? (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-green-800">Live Credentials</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Ready to generate! You have production domains configured.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-red-800">Live Credentials</span>
                    </div>
                    <p className="text-sm text-red-700">
                      Not available. Add a production domain (e.g., https://myapp.com) to enable Live credentials.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/api-docs"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Go to API Documentation
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
